import React, { useState } from 'react';
import { useTranslate } from 'nucleus-text';
import Router from 'next/router';
import { TemplateActions as Actions } from '@cvent/carina/components/Templates/TemplateActions';
import { getIllustrationFromType } from '@cvent/carina/components/Templates/getIllustrationFromType';
import { IllustrationContent } from '@cvent/carina/components/Templates/IllustrationContent';
import { IllustrationErrorDetails } from '@cvent/carina/components/Templates/IllustrationErrorDetails';
import { IllustrationNotice, IllustrationNoticeProps } from '@cvent/carina/components/Templates/IllustrationNotice';
import { IllustrationHeader } from '@cvent/carina/components/Templates/IllustrationHeader';
import { Body } from '@cvent/carina/components/Templates/Body';
import { Main } from '@cvent/carina/components/Templates/Main';
import { ContentArea } from '@cvent/carina/components/Templates/ContentArea';
import { Page } from '@cvent/carina/components/Templates/Page';
import { Dock } from '@cvent/carina/components/Templates/Dock';
import { CSSObject } from '@emotion/react';
import { ActionType } from '@cvent/carina/components/Templates/utils/helpers';
import { useThemeValues } from '@context/ThemeProvider';

interface ComposedErrorProps {
  // Carina-defined error
  errorType: string;
  // The page load id
  httpLogPageLoadId: string;
  // The request id
  httpLogRequestId: string;
  // Will override the default Carina image defined by 'errorType'
  customImage?: IllustrationNoticeProps['customIllustration'];
  // The pre-translated title to display
  errorTitle?: string;
  // The pre-translated content to display
  errorContent?: string;
  // On-screen actions the user can take - defaults to a 'Go back' button
  customActions?: Array<ActionType>;
}

const useStyles = (): Record<string, CSSObject> => ({ additionalErrorDetails: { marginBottom: -48 } });

/**
 * Composed Error
 * For errors handled within our AppLayout (error states)
 */
function ComposedError({ errorType, httpLogPageLoadId, httpLogRequestId, ...props }: ComposedErrorProps): JSX.Element {
  const [now] = useState(new Date());
  const { translate } = useTranslate();
  const styles = useStyles();

  const { customImage } = props;
  const { errorTitle } = props;
  const { errorContent } = props;
  const { customActions } = props;

  const { hideIllustrations } = useThemeValues();

  return (
    <Main>
      <Body>
        <ContentArea>
          <IllustrationNotice
            title={errorType}
            customIllustration={!hideIllustrations ? customImage : undefined}
            illustration={!hideIllustrations ? getIllustrationFromType(errorType) : undefined}
          >
            <IllustrationHeader>{errorTitle || translate('Error-Page-Title')}</IllustrationHeader>
            <IllustrationContent>{errorContent || translate('Error-Page-Content')}</IllustrationContent>
            <Actions
              actions={
                customActions || [
                  {
                    value: translate('go_back'),
                    onClick: (): void => {
                      Router.back();
                    }
                  }
                ]
              }
              position="center"
            />
            <div css={styles.additionalErrorDetails}>
              <IllustrationErrorDetails heading="httpLogPageLoadId:" instanceID={httpLogPageLoadId} />
            </div>
            <IllustrationErrorDetails
              heading="httpLogRequestId:"
              instanceID={httpLogRequestId}
              dateHeading="timestamp:"
              date={now.toISOString()}
            />
          </IllustrationNotice>
        </ContentArea>
      </Body>
    </Main>
  );
}

// Page-level errors that occupy the entire view
export function PageError(props: ComposedErrorProps): JSX.Element {
  return (
    <Page>
      <ComposedError {...props} />
    </Page>
  );
}

// Component-level errors that still leave room for header, footer, and navigation
export function DockError(props: ComposedErrorProps): JSX.Element {
  return (
    <Dock>
      <ComposedError {...props} />
    </Dock>
  );
}

export default ComposedError;
